import { Input, Table, Checkbox, Dropdown, Spin } from "antd";
import { LeftOutlined, RightOutlined } from "@ant-design/icons";
import { useState, useCallback, useEffect, useRef, useMemo } from "react";
import { ChevronIcon } from "../assets/icon";
import filter from "../assets/filter.svg";
import SearchIcon from "../assets/search-normal.svg";

const CommonTable = ({
  handlePageChange,
  pagination,
  body,
  columns,
  tableLoading = false,
  isPagination = true,

  // Search props
  showSearch = false,
  searchValue = "",
  onSearchChange,

  // Status filter props
  showStatusFilter = false,
  statusOptions = [],
  statusOptionsLoading = false,
  statusValue,
  onStatusChange,
  onStatusFilterOpen,
}) => {
  // Local search state for UI-only mode (no onSearchChange provided)
  const [localSearch, setLocalSearch] = useState(searchValue);
  const [statusOpen, setStatusOpen] = useState(false);
  const [tempStatusValue, setTempStatusValue] = useState(statusValue || []);
  const timerRef = useRef(null);

  // Sync local search with external value
  useEffect(() => {
    setLocalSearch(searchValue);
  }, [searchValue]);

  // Sync temp status with applied status when dropdown opens
  useEffect(() => {
    if (statusOpen) {
      setTempStatusValue(statusValue || []);
    }
  }, [statusOpen, statusValue]);

  // Cleanup timer on unmount
  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleSearchInput = useCallback(
    (e) => {
      const val = e.target.value;
      setLocalSearch(val);

      if (onSearchChange) {
        if (timerRef.current) clearTimeout(timerRef.current);
        timerRef.current = setTimeout(() => {
          onSearchChange(val.trim());
        }, 500);
      }
    },
    [onSearchChange],
  );

  const handleToggleStatus = (value) => {
    setTempStatusValue((prev) =>
      prev.includes(value) ? prev.filter((v) => v !== value) : [...prev, value],
    );
  };

  const handleApply = () => {
    if (onStatusChange) {
      // Pass `undefined` when no status is selected so the parent/API can omit the filter.
      const appliedStatus =
        tempStatusValue.length > 0 ? tempStatusValue : undefined;
      onStatusChange(appliedStatus);
      setStatusOpen(false);
    }
  };

  const handleClear = () => {
    setTempStatusValue([]);
    if (onStatusChange) {
      onStatusChange(undefined);
    }
    setStatusOpen(false);
  };

  const customItemRender = (current, type, originalElement) => {
    if (type === "prev") {
      return <LeftOutlined />;
    }
    if (type === "next") {
      return <RightOutlined />;
    }
    return null;
  };

  const statusValueSafe = useMemo(() => statusValue || [], [statusValue]);

  const statusLabel = useMemo(() => {
    if (statusValueSafe.length === 0) return "All status";
    if (statusValueSafe.length === 1) {
      const opt = statusOptions.find((o) => o.value === statusValueSafe[0]);
      return opt ? opt.label : "All status";
    }
    return `${statusValueSafe.length} Selected`;
  }, [statusValueSafe, statusOptions]);

  const statusMenu = (
    <div className="status-dropdown-menu">
      {statusOptionsLoading ? (
        <div className="d-flex justify-content-center align-items-center py-4 px-3">
          <Spin size="small" />
        </div>
      ) : (
        <>
          <ul className="status-dropdown-list">
            {statusOptions.map((opt) => (
              <li key={opt.value} onClick={() => handleToggleStatus(opt.value)}>
                <div className="status-option">
                  <span>{opt.label}</span>
                  <Checkbox
                    className="campaign-modal-checkbox"
                    checked={tempStatusValue.includes(opt.value)}
					style={{ pointerEvents: "none" }}
					
                  />
                </div>
              </li>
            ))}
          </ul>
          <div className="status-dropdown-footer">
            <button
              className=" primarybtn campaignBtn fs-14"
              style={{
                backgroundColor: "#fff",
                color: "#cd6422",
                border: "1px solid #cd6422",
              }}
              type="text"
              onClick={handleClear}
            >
              Clear filters
            </button>
            <button
              className=" primarybtn campaignBtn gap-2 align-items-center fs-14"
              style={{
                backgroundColor: "#cd6422",
                color: "#fff",
              }}
              type="primary"
              onClick={handleApply}
            >
              Apply
            </button>
          </div>
        </>
      )}
    </div>
  );

  const handleStatusDropdownVisibleChange = (open) => {
    setStatusOpen(open);
    if (open && onStatusFilterOpen) {
      onStatusFilterOpen();
    }
  };

  const showToolbar = showSearch || showStatusFilter;

  return (
    <>
      {showToolbar && (
        <div className="d-flex align-items-center justify-content-between gap-3 mb-2">
          {showSearch && (
            <Input
              className="table-search-input form-control"
              placeholder="Please search here"
              prefix={<img src={SearchIcon} alt="Search Icon" />}
              value={localSearch}
              allowClear
              onChange={handleSearchInput}
              style={{ flex: 1 }}
            />
          )}
          {showStatusFilter && (
            <div className="d-flex align-items-center tableStatusFilterWrapper">
              {/* <StatusFilterIcon /> */}
              <img src={filter} alt="Status Filter Icon" />
              <Dropdown
                overlay={statusMenu}
                trigger={["click"]}
                visible={statusOpen}
                onVisibleChange={handleStatusDropdownVisibleChange}
                placement="bottomRight"
              >
                <div className="tableStatusDropdownTrigger">
                  <span className="selectedStatusText">{statusLabel}</span>
                  <ChevronIcon isOpen={statusOpen} />
                </div>
              </Dropdown>
            </div>
          )}
        </div>
      )}
      <Table
        className="table-layout"
        columns={columns}
        dataSource={body}
        loading={tableLoading}
        rowClassName="comman-table-row"
        pagination={
          isPagination
            ? {
                ...pagination,
                onChange: handlePageChange,
                itemRender: customItemRender,
              }
            : false
        }
        sortDirections={["ascend", "descend", "ascend"]}
        showSorterTooltip={false}
      />
    </>
  );
};

export default CommonTable;
